// Currency and Region Configuration Data
import { Currency, Region, CurrencyCode, RegionCode } from '../types/region';

// Supported Currencies
export const CURRENCIES: Record<CurrencyCode, Currency> = {
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    locale: 'en-IN',
    decimalPlaces: 0
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    decimalPlaces: 2
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE',
    decimalPlaces: 2
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    decimalPlaces: 2
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    locale: 'en-AU',
    decimalPlaces: 2
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    locale: 'en-CA',
    decimalPlaces: 2
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    name: 'Singapore Dollar',
    locale: 'en-SG',
    decimalPlaces: 2
  },
  AED: {
    code: 'AED',
    symbol: 'د.إ',
    name: 'UAE Dirham',
    locale: 'ar-AE',
    decimalPlaces: 2
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    locale: 'ms-MY',
    decimalPlaces: 2
  }
};

// Supported Regions
export const REGIONS: Record<RegionCode, Region> = {
  IN: {
    code: 'IN',
    name: 'India',
    currency: 'INR',
    flag: '🇮🇳',
    locale: 'en-IN',
    timezone: 'Asia/Kolkata',
    phoneFormat: '+91 XXXXX XXXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  US: {
    code: 'US',
    name: 'United States',
    currency: 'USD',
    flag: '🇺🇸',
    locale: 'en-US',
    timezone: 'America/New_York',
    phoneFormat: '+1 (XXX) XXX-XXXX',
    dateFormat: 'MM/DD/YYYY'
  },
  GB: {
    code: 'GB',
    name: 'United Kingdom',
    currency: 'GBP',
    flag: '🇬🇧',
    locale: 'en-GB',
    timezone: 'Europe/London',
    phoneFormat: '+44 XXXX XXXXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  EU: {
    code: 'EU',
    name: 'Europe',
    currency: 'EUR',
    flag: '🇪🇺',
    locale: 'en-EU',
    timezone: 'Europe/Paris',
    phoneFormat: '+XX XXX XXX XXX',
    dateFormat: 'DD/MM/YYYY'
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    currency: 'AUD',
    flag: '🇦🇺',
    locale: 'en-AU',
    timezone: 'Australia/Sydney',
    phoneFormat: '+61 XXX XXX XXX',
    dateFormat: 'DD/MM/YYYY'
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    currency: 'CAD',
    flag: '🇨🇦',
    locale: 'en-CA',
    timezone: 'America/Toronto',
    phoneFormat: '+1 (XXX) XXX-XXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  SG: {
    code: 'SG',
    name: 'Singapore',
    currency: 'SGD',
    flag: '🇸🇬',
    locale: 'en-SG',
    timezone: 'Asia/Singapore',
    phoneFormat: '+65 XXXX XXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  AE: {
    code: 'AE',
    name: 'United Arab Emirates',
    currency: 'AED',
    flag: '🇦🇪',
    locale: 'ar-AE',
    timezone: 'Asia/Dubai',
    phoneFormat: '+971 XX XXX XXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  MY: {
    code: 'MY',
    name: 'Malaysia',
    currency: 'MYR',
    flag: '🇲🇾',
    locale: 'ms-MY',
    timezone: 'Asia/Kuala_Lumpur',
    phoneFormat: '+60 XX-XXX XXXX',
    dateFormat: 'DD/MM/YYYY'
  },
  GLOBAL: {
    code: 'GLOBAL',
    name: 'Global',
    currency: 'USD',
    flag: '🌐',
    locale: 'en-US',
    timezone: 'UTC',
    phoneFormat: '+XXX XXX XXX XXX',
    dateFormat: 'DD/MM/YYYY'
  }
};

// Exchange rates (base: INR) for DISPLAY conversion only — checkout is always
// billed in INR (Razorpay). Approximate; refresh periodically (no live feed).
//
// USD is the anchor and is kept in lockstep with tzilla-be's USD_TO_INR
// constant (src/constants/index.ts) — currently 86, i.e. 1 USD = 86 INR, so
// USD = 1 / 86. If that backend constant changes, change USD here to match and
// re-derive the rest.
const USD_TO_INR = 86;
export const EXCHANGE_RATES: Record<CurrencyCode, number> = {
  INR: 1,
  USD: 1 / USD_TO_INR,          // 1 USD = 86 INR
  EUR: 1 / (USD_TO_INR * 1.09), // 1 EUR ≈ 1.09 USD  → ≈ 93.7 INR
  GBP: 1 / (USD_TO_INR * 1.27), // 1 GBP ≈ 1.27 USD  → ≈ 109 INR
  AUD: 1 / (USD_TO_INR * 0.66), // 1 AUD ≈ 0.66 USD  → ≈ 57 INR
  CAD: 1 / (USD_TO_INR * 0.73), // 1 CAD ≈ 0.73 USD  → ≈ 63 INR
  SGD: 1 / (USD_TO_INR * 0.76), // 1 SGD ≈ 0.76 USD  → ≈ 65 INR
  AED: 1 / (USD_TO_INR / 3.6725), // AED pegged at 3.6725 / USD → ≈ 23.4 INR
  MYR: 1 / (USD_TO_INR / 4.45),   // 1 USD ≈ 4.45 MYR → ≈ 19.3 INR
};

// Regional pricing adjustments (multipliers)
// Some regions may have different pricing strategies
export const REGIONAL_PRICING_MULTIPLIERS: Record<RegionCode, number> = {
  IN: 1.0,     // Base pricing
  US: 1.0,     // Standard international pricing
  GB: 1.0,     // Standard international pricing
  EU: 1.0,     // Standard international pricing
  AU: 1.0,     // Standard international pricing
  CA: 1.0,     // Standard international pricing
  SG: 1.0,     // Standard international pricing
  AE: 1.0,     // Standard international pricing
  MY: 0.9,     // Slight discount for Malaysia market
  GLOBAL: 1.0  // Standard pricing
};
